import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PortalDsAlert, PortalDsButton } from 'src/components/portal-design-system';
import { ReduxState } from 'src/redux/redux-state';
import { showNotification } from 'src/redux/notification/notification.slice';
import ErrorLogService from 'src/services/error-log/error-log.service';
import { Client } from 'src/pages/client-management/types/client.types';
import { mintStudioHandoffUrl } from './studio-handoff.service';
import {
  dismissStudioHandoffNotice,
  isStudioHandoffDismissed,
} from './studio-handoff-storage';

interface StudioHandoffNoticeProps {
  client: Client | null | undefined;
  className?: string;
}

function extractHandoffError(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return 'Unable to open Studio right now. Please try again shortly.';
  }

  const record = error as {
    response?: { status?: number; data?: { detail?: unknown } };
  };
  const detail = record.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  const status = record.response?.status;
  if (status === 403) {
    return 'You do not have access to open Studio for this client.';
  }
  if (status === 409) {
    return 'Studio handoff is not available for this client yet.';
  }
  if (status === 503) {
    return 'Studio handoff is not configured in this environment.';
  }

  return 'Unable to open Studio right now. Please try again shortly.';
}

export function StudioHandoffNotice({ client, className }: StudioHandoffNoticeProps) {
  const dispatch = useDispatch();
  const userId = useSelector((state: ReduxState) => state.userState.extUserId);
  const accessToken = useSelector((state: ReduxState) => state.userState.access);
  const [dismissed, setDismissed] = useState(() => isStudioHandoffDismissed(userId));
  const [isOpening, setIsOpening] = useState(false);

  if (!client?.studio_ready || client.studio_cutover || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    dismissStudioHandoffNotice(userId);
    setDismissed(true);
  };

  const handleOpenStudio = async() => {
    if (!accessToken) {
      dispatch(showNotification({
        summary: 'Unable to open Studio. Please try again.',
        theme: 'warning',
      }));
      return;
    }

    setIsOpening(true);
    try {
      const url = await mintStudioHandoffUrl(client.ext_client_id, accessToken);
      window.location.assign(url);
    } catch (error) {
      ErrorLogService.logError(error, 'studio-handoff-notice', 'handleOpenStudio', [client.ext_client_id]);
      dispatch(showNotification({
        summary: extractHandoffError(error),
        theme: 'warning',
      }));
      setIsOpening(false);
    }
  };

  return (
    <PortalDsAlert
      variant="info"
      title="Your Studio account is ready"
      onDismiss={handleDismiss}
      className={className}
    >
      <p className="mb-3">
        We have set up your Invisibly Studio workspace. You can continue using this dashboard
        for now, or open Studio to sign in with one click.
      </p>
      <PortalDsButton
        type="button"
        variant="primary"
        size="sm"
        onClick={() => { void handleOpenStudio(); }}
        disabled={isOpening}
      >
        {isOpening ? 'Opening Studio…' : 'Open Studio'}
      </PortalDsButton>
    </PortalDsAlert>
  );
}
